import { Schema } from 'prosemirror-model';
import { Plugin } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { KeyboardEventCode } from '@recruitee/user-interface';

import { EditorButton, RtPMPlugin } from '../../editor/types';
import { MentionServiceInternal } from './mentions-service-internal';
import { MentionsServicePublic } from './mentions-service-public';
import {
  MentionElement,
  MentionListController,
  MentionNavigationKey,
  MentionsPluginEvents,
} from './types';

const mentionRegex = /(^@|\s@)(?!.*\1).*/;

export class MentionsPlugin implements RtPMPlugin {
  public api: { mentionsService: MentionsServicePublic };
  public buttons = {
    toggleMentionList: <EditorButton>{
      icon: 'alternate-email',
      isActive: () => false,
      run: (state, dispatch, origin) => {
        this.api.mentionsService.mentionsWithSearchOpen(origin);
      },
    },
  };
  public eventStream$: Subject<MentionsPluginEvents>;

  private destroy$ = new Subject<void>();
  private destroyed$ = new BehaviorSubject<boolean>(false);
  private editorView: EditorView<Schema>;
  private mentionServiceInternal: MentionServiceInternal;

  private editorPlugin = new Plugin({
    props: {
      handleDOMEvents: {
        keyup: (view, event) => {
          const { state } = view;
          const { $to } = state.selection;
          const text = $to.parent.textBetween(0, $to.parentOffset, '\n', '\n');
          const match = mentionRegex.exec(text);
          this.mentionServiceInternal.mentionsMatchHandler(
            view.state,
            match,
            match ? $to.pos - match[0].length : 0,
            match ? $to.pos : 0,
          );

          return false;
        },
      },
      handleKeyDown: (view, event) => {
        const { key } = event;
        const keyCodeNavigate =
          KeyboardEventCode.Enter === key ||
          KeyboardEventCode.ArrowUp === key ||
          KeyboardEventCode.ArrowDown === key;

        if (keyCodeNavigate && this.mentionListController.isOpen()) {
          this.mentionListController.handleKey(<MentionNavigationKey>key);
          return true;
        }

        return false;
      },
    },
  });

  constructor(
    private events: Subject<MentionsPluginEvents>,
    private dataSource: Observable<MentionElement[]>,
    private mentionListController: MentionListController,
    private scroll$: Observable<void>,
  ) {
    this.eventStream$ = events;
  }

  public afterEditorInit(editorView: EditorView) {
    this.editorView = editorView;
    this.mentionServiceInternal = new MentionServiceInternal(
      editorView,
      this.mentionListController,
      this.dataSource,
      this.eventStream$,
      this.scroll$,
      this.destroy$,
    );
    this.api = {
      mentionsService: new MentionsServicePublic(editorView, this.mentionServiceInternal),
    };
  }

  public destroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public getNativeEditorPlugins() {
    return [this.editorPlugin];
  }
}
